import { createStyles, withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import * as Sentry from '@sentry/react';
import React, { ReactNode } from 'react';
import { JupiterTheme } from '../../theme/types';
import ConditionalWrap from '../util/ConditionalWrap';
import ErrorAlert from './ErrorAlert';

const styles = (theme: JupiterTheme) =>
  createStyles({
    container: {
      margin: theme.spacing(),
    },
    buttonRow: {
      marginTop: theme.spacing(),
      display: 'flex',
      width: '100%',
    },
    button: {
      marginRight: theme.spacing(),
    },
  });

interface Props {
  classes: any;
  children: any;
  fallback?: ReactNode;
  modal?: boolean;
}

interface State {
  error: any;
  errorDetailDialogOpen: boolean;
  modalOpen: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      error: null,
      modalOpen: false,
      errorDetailDialogOpen: false,
    };
  }

  componentDidCatch(error: any, info: any) {
    /*
    React documentation suggests also using a static getDerivedStateFromError method for
    error bounding; however, there seems to be a bug with that API (see link below).
    Unfortunately, using componentDidCatch to set state will apparently be deprecated
    eventually. Hopefully by that time the bug will be fixed.

    https://github.com/facebook/react/issues/14981#issuecomment-472391247
    */
    console.error('error/info:', error, info);

    // Log the error to Sentry
    Sentry.withScope((scope) => {
      scope.setExtras(info);
      Sentry.captureException(error);
    });

    this.setState({
      error,
      modalOpen: this.props.modal || false,
    });
  }

  handleDialogOpen = () => {
    this.setState({
      errorDetailDialogOpen: true,
    });
  };

  handleDialogClose = () => {
    this.setState({
      errorDetailDialogOpen: false,
    });
  };

  reloadPage = () => {
    window.location.reload();
  };

  render() {
    const { classes, fallback } = this.props;
    if (this.state.error) {
      return fallback ? (
        fallback
      ) : (
        <ConditionalWrap
          condition={this.props.modal}
          wrapper={(children: any) => (
            <Dialog
              open={this.state.modalOpen}
              onBackdropClick={() => this.setState({ modalOpen: false })}
            >
              {children}
            </Dialog>
          )}
        >
          <div className={classes.container}>
            <ErrorAlert>
              An unexpected error caused the feature to stop working.
            </ErrorAlert>
            <div className={classes.buttonRow}>
              <Button
                classes={{ root: classes.button }}
                variant="contained"
                color="primary"
                onClick={this.reloadPage}
              >
                Reload
              </Button>
              <Button
                classes={{ root: classes.button }}
                onClick={this.handleDialogOpen}
              >
                Show error details
              </Button>
            </div>
            {this.state.errorDetailDialogOpen && (
              <Dialog
                open
                onClose={this.handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">Error details</DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {this.state.error.message}
                    <br />
                    {this.state.error.stack}
                  </DialogContentText>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </ConditionalWrap>
      );
    }
    return this.props.children;
  }
}

export default withStyles(styles as any)(ErrorBoundary);
